/**
 * The shape of the UI store: what it holds and what it can be asked to do.
 * Kept apart from the store creation so the persistence layer and the setup
 * revision slice can depend on the shape without depending on the store.
 */

import type { ConnectorParameterUndoPlan, FormField, ModalState, Option, Screen } from '../types';
import type { ProjectScopeReconciliation } from '../lib/setupRevision';
import {
  initialSetupRevisionState,
  type SetupRevisionActions,
  type SetupRevisionState,
} from './setupRevisionSlice';
import {
  initialSectionsState,
  type AdditionalCategoriesSection,
  type BoltConnectionSection,
  type ConnectorBindingSection,
  type ProcessPropertiesSection,
  type WorkflowSectionsState,
} from './uiSectionsState';

export interface WizardProgressRestoreSnapshot {
  screen: Screen;
  navigationHistory: Screen[];
  sections: Partial<WorkflowSectionsState>;
}

export interface UIState extends SetupRevisionState {
  // Navigation
  screen: Screen;
  navigationHistory: Screen[];
  selectedOption: Option;
  sections: WorkflowSectionsState;

  // Modal state
  modal: ModalState;

  // Initialization flags
  isInitialized: boolean;

  // Confirmation states
  defaultPropertiesConfirmed: boolean;
  processConditionsChecked: boolean;
  projectPropertiesChecked: boolean;
  connectorParameterUndoPlan: ConnectorParameterUndoPlan | null;
  boltParameterUndoPlan: ConnectorParameterUndoPlan | null;
  additionalCategoryParameterUndoPlan: ConnectorParameterUndoPlan | null;
}

export interface UIActions extends SetupRevisionActions {
  // Navigation actions
  setScreen: (screen: Screen) => void;
  navigateTo: (screen: Screen) => void;
  jumpTo: (screen: Screen, history?: Screen[]) => void;
  goBack: () => void;
  setNavigationHistory: (history: Screen[]) => void;
  clearNavigationHistory: () => void;
  setConnectorBindingSection: (section: ConnectorBindingSection) => void;
  setBoltConnectionSection: (section: BoltConnectionSection) => void;
  setProcessPropertiesSection: (section: ProcessPropertiesSection) => void;
  setAdditionalCategoriesSection: (section: AdditionalCategoriesSection) => void;
  restoreWizardProfileProgress: (progress: WizardProgressRestoreSnapshot | null | undefined) => void;

  // Option actions
  setSelectedOption: (option: Option) => void;
  selectWorkflowFromMenu: (option: Option) => void;

  // Modal actions
  openModal: (modalType: string, modalData?: Record<string, unknown>) => void;
  closeModal: () => void;

  // Initialization actions
  setInitialized: (isInitialized: boolean) => void;

  // Confirmation actions
  setDefaultPropertiesConfirmed: (confirmed: boolean) => void;
  setProcessConditionsChecked: (checked: boolean) => void;
  setProjectPropertiesChecked: (checked: boolean) => void;
  setConnectorParameterUndoPlan: (plan: ConnectorParameterUndoPlan | null) => void;
  setBoltParameterUndoPlan: (plan: ConnectorParameterUndoPlan | null) => void;
  setAdditionalCategoryParameterUndoPlan: (plan: ConnectorParameterUndoPlan | null) => void;
  resetProjectScopedRuntimeState: () => void;
  /**
   * Adopts hydrated project-scoped state when it belongs to the active project
   * and resets it only when it provably belongs to another one. Returns the
   * decision so the caller can skip the work a reset would have made necessary.
   */
  reconcileProjectScopedRuntimeState: (
    activeProjectKey: string | null
  ) => ProjectScopeReconciliation;

  // Reset action
  reset: () => void;
}

export type UIStore = UIState & UIActions;

/** Kept for the store's `modal` field so `FormField` stays part of the contract. */
export type UIFormField = FormField;

export const initialState: UIState = {
  screen: 'welcome',
  navigationHistory: ['welcome'],
  selectedOption: null,
  sections: initialSectionsState,
  modal: {
    isOpen: false,
    modalType: null,
    modalData: undefined,
  },
  isInitialized: false,
  defaultPropertiesConfirmed: false,
  processConditionsChecked: false,
  projectPropertiesChecked: false,
  connectorParameterUndoPlan: null,
  boltParameterUndoPlan: null,
  additionalCategoryParameterUndoPlan: null,
  ...initialSetupRevisionState,
};
